import { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, CalendarDays, Euro, Users } from "lucide-react";

type BookingRow = {
  id: string;
  client_name: string | null;
  client_email: string | null;
  appointment_date: string | null;
  appointment_time: string | null;
  status: string | null;
  amount_cents: number | null;
  service_type: string | null;
};

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const StatCard = ({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number | string }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
    <div className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-serif text-2xl text-foreground">{value}</p>
      </div>
    </div>
  </div>
);

const BusinessDashboard = () => {
  const { user, displayName } = useAuth();
  const [bookings, setBookings] = useState<BookingRow[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    let activo = true;

    const cargar = async () => {
      setCargando(true);
      setError(null);

      const { data: perfil } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!perfil?.id) {
        if (activo) {
          setBookings([]);
          setCargando(false);
        }
        return;
      }

      const { data, error: errorReservas } = await supabase
        .from("bookings")
        .select("id, client_name, client_email, appointment_date, appointment_time, status, amount_cents, service_type")
        .eq("therapist_id", perfil.id)
        .order("appointment_date", { ascending: false });

      if (!activo) return;
      if (errorReservas) {
        setError("No hemos podido cargar tus reservas. Intentalo de nuevo en unos minutos.");
      } else {
        setBookings((data as BookingRow[]) || []);
      }
      setCargando(false);
    };

    void cargar();
    return () => {
      activo = false;
    };
  }, [user]);

  // Ingresos reales agregados por mes a partir de las reservas del terapeuta
  const ingresosPorMes = useMemo(() => {
    const acumulado = new Map<string, { etiqueta: string; total: number; reservas: number }>();

    for (const reserva of bookings) {
      if (!reserva.appointment_date) continue;
      const fecha = new Date(reserva.appointment_date);
      if (Number.isNaN(fecha.getTime())) continue;
      const clave = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}`;
      const etiqueta = `${MESES[fecha.getMonth()]} ${fecha.getFullYear()}`;
      const previo = acumulado.get(clave) || { etiqueta, total: 0, reservas: 0 };
      previo.total += (reserva.amount_cents || 0) / 100;
      previo.reservas += 1;
      acumulado.set(clave, previo);
    }

    return Array.from(acumulado.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([clave, valor]) => ({ clave, ...valor }));
  }, [bookings]);

  const totalIngresos = useMemo(
    () => bookings.reduce((suma, reserva) => suma + (reserva.amount_cents || 0) / 100, 0),
    [bookings],
  );

  const formatoEuro = (valor: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(valor);

  return (
    <>
      <SEO
        title="Mi negocio · MassHappy"
        description="Panel del terapeuta: reservas e ingresos reales de tu actividad en MassHappy."
        noindex
      />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <header className="mb-8">
          <h1 className="font-serif text-3xl text-foreground md:text-4xl">Mi negocio</h1>
          <p className="mt-2 text-muted-foreground">
            {displayName ? `Hola, ${displayName}. ` : ""}Aqui tienes tus reservas y tus ingresos.
          </p>
        </header>

        {cargando ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            <span className="sr-only">Cargando</span>
          </div>
        ) : error ? (
          <p className="rounded-2xl border border-border bg-card p-6 text-muted-foreground">{error}</p>
        ) : (
          <>
            <div className="mb-8 grid gap-4 sm:grid-cols-3">
              <StatCard icon={CalendarDays} label="Reservas" value={bookings.length} />
              <StatCard icon={Euro} label="Ingresos totales" value={formatoEuro(totalIngresos)} />
              <StatCard icon={Users} label="Meses con actividad" value={ingresosPorMes.length} />
            </div>

            <Tabs defaultValue="reservas">
              <TabsList>
                <TabsTrigger value="reservas">Reservas</TabsTrigger>
                <TabsTrigger value="ingresos">Ingresos</TabsTrigger>
              </TabsList>

              <TabsContent value="reservas" className="mt-6">
                {bookings.length === 0 ? (
                  <p className="rounded-2xl border border-border bg-card p-6 text-muted-foreground">
                    Todavia no tienes reservas. Cuando alguien reserve contigo, aparecera aqui.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {bookings.map((reserva) => (
                      <li key={reserva.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-medium text-foreground">{reserva.client_name || "Sin nombre"}</p>
                            <p className="text-sm text-muted-foreground">
                              {reserva.appointment_date} {reserva.appointment_time?.slice(0, 5)} · {reserva.service_type}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-serif text-lg text-foreground">
                              {formatoEuro((reserva.amount_cents || 0) / 100)}
                            </span>
                            {reserva.status && <Badge variant="secondary">{reserva.status}</Badge>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="ingresos" className="mt-6">
                {ingresosPorMes.length === 0 ? (
                  <p className="rounded-2xl border border-border bg-card p-6 text-muted-foreground">
                    Todavía no hay ingresos registrados. Aquí verás tus reservas cuando empieces a recibirlas.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {ingresosPorMes.map((mes) => (
                      <li
                        key={mes.clave}
                        className="flex items-center justify-between rounded-2xl border border-border bg-card p-5 shadow-soft"
                      >
                        <div>
                          <p className="font-medium text-foreground">{mes.etiqueta}</p>
                          <p className="text-sm text-muted-foreground">
                            {mes.reservas} {mes.reservas === 1 ? "reserva" : "reservas"}
                          </p>
                        </div>
                        <span className="font-serif text-xl text-foreground">{formatoEuro(mes.total)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
      <Footer />
    </>
  );
};

export default BusinessDashboard;
